import config from '~~/configs/collections.config';
import usePageStore from '~/stores/page';
import type { ICmsAuthors, ICmsDossiers, ICmsTags, ICmsThemes } from '~~/types/_cms-types';
const messages = {
  notFound: {
    statusCode: 404,
    statusMessage: 'Instantie niet gevonden',
  },
  archived: {
    statusCode: 410,
    statusMessage: 'Instantie is gearchiveerd',
    message: 'Deze instantie is gearchiveerd en niet meer beschikbaar',
  },
};

export default async function useCollectionInstance(
  collection: 'themes' | 'opinionThemes' | 'dossiers' | 'tags' | 'authors',
): Promise<ICmsThemes | ICmsDossiers | ICmsTags | ICmsAuthors | undefined> {
  const cms = useCms();
  const { objectKeysToCamelCase } = formatUtils();
  const { setCurrentPage } = usePageStore();
  const { slug } = useRoute().params;
  const parent = useIsChildPageOf();
  // From Directus, fetch themes when using opinionThemes
  const directusCollection = collection === 'opinionThemes' ? 'themes' : collection;

  const fetchCollectionInstance = async (id: number | string) => {
    return await cms.readItem(directusCollection, id, { fields: config[collection] || ['*'] });
  };

  if (!slug) {
    throw new Error('Thema niet gevonden');
  }

  const cacheKey = `${collection}${parent !== '' ? `-${parent}` : ''}-${slug}`;
  const cachedInstance = useNuxtData(cacheKey).data.value;

  let instance = cachedInstance ? objectKeysToCamelCase(cachedInstance) : undefined;

  if (!instance) {
    const id = (slug as string).split('-').at(-1);
    if (!id || isNaN(parseInt(id, 10))) {
      throwError(
        messages.notFound.statusCode,
        messages.notFound.statusMessage,
        messages.notFound.statusMessage,
      );
      return;
    }

    const { data, error } = await useAsyncData(cacheKey, async () => {
      try {
        return await fetchCollectionInstance(id);
      } catch (error) {
        console.error(error);
        throwFetchError(error, messages.notFound);
      }
    });

    if (error.value) {
      throw error.value;
    }

    instance = data.value ? objectKeysToCamelCase(data.value) : undefined;
  }

  if (instance?.instance?.status === 'archived') {
    const { statusCode, statusMessage, message } = messages.archived;
    throwError(statusCode, statusMessage, message);
  }

  // Update the current page state
  setCurrentPage(undefined);

  return instance;
}
